import React from 'react';
import { InstagramWaveInfluencerPost } from '@round/api';
import Table from 'ui/NewTable/Table';
import { CellProps, Column } from 'react-table';
import { formatDate, numberWithCommas, roundTo2Dp } from 'helpers';
import styles from '../MicrosStatsBar/MicroCreatorsPostsTable/MicroCreatorsPostsTable.module.css';
import { getTotalValue } from '../MicrosStatsBar/helpers';
import { filterByVisibility } from 'utility/utility';
import { useCheckUserGroupsAccess } from 'Modules/Auth/hooks/useCheckUserGroupsAccess';

// Instagram and YouTube wave posts share a shape (views/likes/comments), so one table
// serves both. YouTube's author_username is the channel title, Instagram's the handle;
// neither block carries a profile id, so the account is shown as text and the post URL
// is the link.
type Props = {
    posts: InstagramWaveInfluencerPost[];
};

const getEngagementRate = (post: Pick<InstagramWaveInfluencerPost, 'like_count' | 'comment_count' | 'view_count'>) => {
    if (!post.view_count) {
        return null;
    }

    return ((post.like_count ?? 0) + (post.comment_count ?? 0)) / post.view_count;
};

const WaveInstagramYoutubePostsTable = ({ posts }: Props) => {
    const isStaff = useCheckUserGroupsAccess(['influencer_editor']);

    const columns: Column<InstagramWaveInfluencerPost>[] = filterByVisibility([
        {
            Header: 'Account',
            accessor: 'author_username',
            Cell: ({ value }) => <>{value?.length ? value : '-'}</>,
        },
        {
            Header: 'Post',
            accessor: 'url',
            Cell: ({ value }) => (
                <a href={value} target="_blank" rel="noopener noreferrer">
                    {value}
                </a>
            ),
        },
        {
            Header: 'Post date',
            accessor: 'create_time',
            isVisible: !!isStaff,
            Cell: ({ value }) => <>{value ? formatDate(value) : '-'}</>,
        },
        {
            Header: 'Views',
            accessor: 'view_count',
            Cell: ({ value }) => <>{typeof value === 'number' ? numberWithCommas(value) : '-'}</>,
        },
        {
            Header: 'Total engagements',
            id: 'total_engagement',
            Cell: ({ row: { original } }: CellProps<InstagramWaveInfluencerPost>) => {
                const { like_count, comment_count } = original;
                if (like_count === null && comment_count === null) {
                    return <>-</>;
                }

                return <>{numberWithCommas((like_count ?? 0) + (comment_count ?? 0))}</>;
            },
        },
        {
            Header: 'Engagement rate',
            id: 'engagement_rate',
            Cell: ({ row: { original } }: CellProps<InstagramWaveInfluencerPost>) => {
                const rate = getEngagementRate(original);
                return <>{rate === null ? '-' : `${roundTo2Dp(rate * 100)}%`}</>;
            },
        },
        {
            Header: 'Likes',
            accessor: 'like_count',
            Cell: ({ value }) => <>{typeof value === 'number' ? numberWithCommas(value) : '-'}</>,
        },
        {
            Header: 'Comments',
            accessor: 'comment_count',
            Cell: ({ value }) => <>{typeof value === 'number' ? numberWithCommas(value) : '-'}</>,
        },
    ]);

    return (
        <Table
            tableClassName={styles.table}
            disableSortBy
            data={posts}
            columns={columns}
            renderFooter={() => {
                const views = getTotalValue(posts, 'view_count');
                const likes = getTotalValue(posts, 'like_count');
                const comments = getTotalValue(posts, 'comment_count');
                const totalEngagements = likes === null && comments === null ? null : (likes ?? 0) + (comments ?? 0);
                const engagementRate = getEngagementRate({
                    like_count: likes,
                    comment_count: comments,
                    view_count: views,
                });

                return (
                    <tfoot>
                        <tr>
                            <td colSpan={isStaff ? 3 : 2} />
                            <td>{typeof views === 'number' ? numberWithCommas(views) : '-'}</td>
                            <td>{typeof totalEngagements === 'number' ? numberWithCommas(totalEngagements) : '-'}</td>
                            <td>{engagementRate === null ? '-' : `${roundTo2Dp(engagementRate * 100)}%`}</td>
                            <td>{typeof likes === 'number' ? numberWithCommas(likes) : '-'}</td>
                            <td>{typeof comments === 'number' ? numberWithCommas(comments) : '-'}</td>
                        </tr>
                    </tfoot>
                );
            }}
        />
    );
};

export default WaveInstagramYoutubePostsTable;
